import React, { useState, useEffect, useCallback } from 'react';
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Tooltip } from 'recharts';
import { useTenant } from '../components/Layout';
import NoTenantGuard from '../components/NoTenantGuard';
import { getGovernanceFramework, getPillarRemediation, getAIRemediationGuide } from '../api';
import AIGovernanceDetailedReport from '../components/AIGovernanceDetailedReport';

const PILLAR_COLORS = {
  identity_access: '#6366f1',
  management_structure: '#8b5cf6',
  networking_connectivity: '#06b6d4',
  security_compliance: '#ef4444',
  governance_policy: '#f59e0b',
  monitoring_observability: '#10b981',
  operations_resilience: '#f97316',
  devops_automation: '#3b82f6'
};

const PILLAR_ICONS = {
  identity_access: '\u{1F511}',
  management_structure: '\u{1F3D7}',
  networking_connectivity: '\u{1F310}',
  security_compliance: '\u{1F6E1}',
  governance_policy: '\u{1F4DC}',
  monitoring_observability: '\u{1F50D}',
  operations_resilience: '\u{2699}',
  devops_automation: '\u{1F680}'
};

const RISK_STYLE = {
  mature: { bg: '#dcfce7', color: '#166534', label: 'Mature', border: '#86efac' },
  moderate: { bg: '#fef3c7', color: '#92400e', label: 'Moderate Risk', border: '#fcd34d' },
  high: { bg: '#fee2e2', color: '#991b1b', label: 'High Risk', border: '#fca5a5' },
};

const PRIORITY_STYLE = {
  high: { bg: '#fee2e2', color: '#991b1b', dot: '#ef4444' },
  medium: { bg: '#fef3c7', color: '#92400e', dot: '#f59e0b' },
  low: { bg: '#dbeafe', color: '#1e40af', dot: '#3b82f6' },
};

function MaturityBadge({ score }) {
  let bg, color;
  if (score >= 4) { bg = '#dcfce7'; color = '#166534'; }
  else if (score >= 3) { bg = '#fef3c7'; color = '#92400e'; }
  else { bg = '#fee2e2'; color = '#991b1b'; }
  return (
    <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 700, background: bg, color }}>
      {score}/5
    </span>
  );
}

function OverallGauge({ percentage, maturity, risk }) {
  const rs = RISK_STYLE[risk] || RISK_STYLE.high;
  const circumference = 2 * Math.PI * 54;
  const offset = circumference - (percentage / 100) * circumference;
  let strokeColor;
  if (percentage >= 80) strokeColor = '#10b981';
  else if (percentage >= 60) strokeColor = '#f59e0b';
  else strokeColor = '#ef4444';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
      <svg width="130" height="130" viewBox="0 0 130 130">
        <circle cx="65" cy="65" r="54" fill="none" stroke="#e5e7eb" strokeWidth="10" />
        <circle cx="65" cy="65" r="54" fill="none" stroke={strokeColor} strokeWidth="10"
          strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={offset}
          transform="rotate(-90 65 65)" style={{ transition: 'stroke-dashoffset 0.8s ease' }} />
        <text x="65" y="58" textAnchor="middle" fontSize="26" fontWeight="700" fill="#1f2937">{percentage}%</text>
        <text x="65" y="78" textAnchor="middle" fontSize="11" fill="#6b7280">Maturity</text>
      </svg>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 600, background: rs.bg, color: rs.color, border: `1px solid ${rs.border}` }}>
          {rs.label}
        </span>
        <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>{maturity}/5 avg</span>
      </div>
    </div>
  );
}

function ScoreUpliftBar({ current, potential, label, color }) {
  return (
    <div style={{ flex: 1 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: '0.7rem', color: '#6b7280' }}>{label}</span>
        <span style={{ fontSize: '0.75rem', fontWeight: 700, color }}>{potential}/5</span>
      </div>
      <div style={{ position: 'relative', height: 8, borderRadius: 4, background: '#e5e7eb' }}>
        <div style={{ position: 'absolute', height: '100%', borderRadius: 4, background: '#d1d5db', width: `${(current / 5) * 100}%`, transition: 'width 0.4s' }} />
        <div style={{ position: 'absolute', height: '100%', borderRadius: 4, background: color, width: `${(potential / 5) * 100}%`, opacity: 0.4, transition: 'width 0.4s' }} />
      </div>
    </div>
  );
}

function AIRemediationModal({ guide, onClose, pillarColor }) {
  if (!guide) return null;
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '1rem',
    }} onClick={onClose}>
      <div style={{
        background: '#fff', borderRadius: 12, width: '100%', maxWidth: 720,
        maxHeight: '85vh', overflow: 'auto', boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
      }} onClick={e => e.stopPropagation()}>
        <div style={{
          position: 'sticky', top: 0, background: '#fff', padding: '1.25rem 1.5rem 0.75rem',
          borderBottom: '1px solid #e5e7eb', zIndex: 1, borderRadius: '12px 12px 0 0',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                <span style={{ fontSize: '1rem' }}>&#10024;</span>
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#1f2937' }}>AI Remediation Guide</h3>
              </div>
              <p style={{ margin: 0, fontSize: '0.8rem', color: '#6b7280' }}>{guide.resourceName}</p>
            </div>
            <button onClick={onClose} style={{
              background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer',
              color: '#9ca3af', padding: '0.25rem', lineHeight: 1,
            }}>&times;</button>
          </div>
        </div>

        <div style={{ padding: '1.25rem 1.5rem' }}>
          <div style={{
            background: '#f0f4ff', borderRadius: 8, padding: '0.85rem 1rem',
            marginBottom: '1rem', border: '1px solid #dbeafe',
          }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#3b82f6', textTransform: 'uppercase', marginBottom: '0.3rem' }}>Summary</div>
            <p style={{ margin: 0, fontSize: '0.82rem', color: '#374151', lineHeight: 1.5 }}>{guide.summary}</p>
          </div>

          <div style={{
            background: '#f0fdf4', borderRadius: 8, padding: '0.85rem 1rem',
            marginBottom: '1.25rem', border: '1px solid #bbf7d0',
          }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#059669', textTransform: 'uppercase', marginBottom: '0.3rem' }}>Score Impact</div>
            <p style={{ margin: 0, fontSize: '0.82rem', color: '#166534', lineHeight: 1.5 }}>{guide.scoreImpact}</p>
          </div>

          <h4 style={{ margin: '0 0 0.75rem', fontSize: '0.9rem', fontWeight: 700, color: '#1f2937' }}>Step-by-Step Remediation</h4>

          {(guide.steps || []).map((step, idx) => (
            <div key={idx} style={{
              marginBottom: '1rem', borderRadius: 8, border: '1px solid #e5e7eb',
              overflow: 'hidden',
            }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: '0.6rem',
                padding: '0.7rem 1rem', background: '#f9fafb', borderBottom: '1px solid #e5e7eb',
              }}>
                <span style={{
                  width: 24, height: 24, borderRadius: '50%', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem',
                  fontWeight: 700, color: '#fff', background: pillarColor || '#6366f1', flexShrink: 0,
                }}>{step.stepNumber}</span>
                <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#1f2937' }}>{step.title}</span>
                {step.estimatedTime && (
                  <span style={{
                    marginLeft: 'auto', fontSize: '0.68rem', color: '#9ca3af',
                    background: '#f3f4f6', padding: '2px 8px', borderRadius: 10,
                  }}>{step.estimatedTime}</span>
                )}
              </div>
              <div style={{ padding: '0.75rem 1rem' }}>
                <p style={{ margin: '0 0 0.5rem', fontSize: '0.8rem', color: '#374151', lineHeight: 1.5 }}>{step.description}</p>

                {step.portalSteps && step.portalSteps.length > 0 && (
                  <div style={{ marginBottom: '0.5rem' }}>
                    <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', marginBottom: '0.3rem' }}>Portal Steps</div>
                    <ol style={{ margin: 0, paddingLeft: '1.2rem' }}>
                      {step.portalSteps.map((ps, pi) => (
                        <li key={pi} style={{ fontSize: '0.78rem', color: '#4b5563', marginBottom: '0.2rem', lineHeight: 1.4 }}>{ps}</li>
                      ))}
                    </ol>
                  </div>
                )}

                {step.commands && step.commands.length > 0 && (
                  <div style={{ marginBottom: '0.5rem' }}>
                    <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', marginBottom: '0.3rem' }}>CLI Commands</div>
                    {step.commands.map((cmd, ci) => (
                      <pre key={ci} style={{
                        background: '#1f2937', color: '#e5e7eb', padding: '0.5rem 0.75rem',
                        borderRadius: 6, fontSize: '0.72rem', overflowX: 'auto',
                        margin: '0.2rem 0', lineHeight: 1.5,
                      }}>{cmd}</pre>
                    ))}
                  </div>
                )}

                {step.verificationStep && (
                  <div style={{
                    background: '#fffbeb', borderRadius: 6, padding: '0.5rem 0.75rem',
                    border: '1px solid #fde68a', marginTop: '0.3rem',
                  }}>
                    <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#92400e' }}>Verify: </span>
                    <span style={{ fontSize: '0.75rem', color: '#92400e' }}>{step.verificationStep}</span>
                  </div>
                )}
              </div>
            </div>
          ))}

          {guide.additionalNotes && guide.additionalNotes.length > 0 && (
            <div style={{
              background: '#fefce8', borderRadius: 8, padding: '0.85rem 1rem',
              marginBottom: '1rem', border: '1px solid #fde68a',
            }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#92400e', textTransform: 'uppercase', marginBottom: '0.3rem' }}>Important Notes</div>
              <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
                {guide.additionalNotes.map((n, ni) => (
                  <li key={ni} style={{ fontSize: '0.78rem', color: '#78350f', marginBottom: '0.2rem', lineHeight: 1.4 }}>{n}</li>
                ))}
              </ul>
            </div>
          )}

          {guide.automationTip && (
            <div style={{
              background: '#f5f3ff', borderRadius: 8, padding: '0.85rem 1rem',
              marginBottom: '1rem', border: '1px solid #ddd6fe',
            }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#6d28d9', textTransform: 'uppercase', marginBottom: '0.3rem' }}>Automation Tip</div>
              <p style={{ margin: 0, fontSize: '0.8rem', color: '#5b21b6', lineHeight: 1.5 }}>{guide.automationTip}</p>
            </div>
          )}

          {guide.relatedResources && guide.relatedResources.length > 0 && (
            <div style={{ marginBottom: '0.5rem' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', marginBottom: '0.3rem' }}>Related Resources</div>
              <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                {guide.relatedResources.map((rr, ri) => (
                  <span key={ri} style={{
                    fontSize: '0.72rem', color: '#374151', background: '#f3f4f6',
                    padding: '3px 10px', borderRadius: 10, border: '1px solid #e5e7eb',
                  }}>{rr}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function RemediationPanel({ tenantId, pillarKey, pillarColor, pillarName, cloudProvider }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [aiLoadingIdx, setAiLoadingIdx] = useState(null);
  const [aiGuide, setAiGuide] = useState(null);
  const [aiError, setAiError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    getPillarRemediation(tenantId, pillarKey)
      .then(res => { setData(res.data); setLoading(false); })
      .catch(() => { setError('Failed to load remediation data'); setLoading(false); });
  }, [tenantId, pillarKey]);

  const handleAskAI = (resource, idx) => {
    setAiLoadingIdx(idx);
    setAiError(null);
    getAIRemediationGuide({
      tenant_id: tenantId,
      pillar_key: pillarKey,
      pillar_name: pillarName || pillarKey,
      resource_name: resource.resource_name,
      resource_type: resource.resource_type,
      resource_group: resource.resource_group || '',
      issue: resource.issue,
      recommendation: resource.recommendation,
      priority: resource.priority,
      cloud_provider: cloudProvider || 'azure',
    })
      .then(res => { setAiGuide(res.data); setAiLoadingIdx(null); })
      .catch(e => {
        const detail = e.response?.data?.detail;
        setAiError(typeof detail === 'string' ? detail : 'Failed to get AI guidance. Please try again.');
        setAiLoadingIdx(null);
      });
  };

  if (loading) return <div style={{ padding: '1rem', textAlign: 'center', color: '#6b7280', fontSize: '0.85rem' }}>Loading remediation data...</div>;
  if (error) return <div style={{ padding: '1rem', textAlign: 'center', color: '#ef4444', fontSize: '0.85rem' }}>{error}</div>;
  if (!data || data.total_issues === 0) return (
    <div style={{ padding: '1.5rem', textAlign: 'center', background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0' }}>
      <span style={{ fontSize: '1.2rem' }}>&#10004;</span>
      <p style={{ margin: '0.5rem 0 0', fontSize: '0.85rem', color: '#166534', fontWeight: 500 }}>No resource-level issues detected for this pillar</p>
    </div>
  );

  const resources = data.impacted_resources || [];

  return (
    <div>
      {aiGuide && <AIRemediationModal guide={aiGuide} onClose={() => setAiGuide(null)} pillarColor={pillarColor} />}
      {aiError && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '0.6rem 1rem', marginBottom: '0.75rem', fontSize: '0.8rem', color: '#991b1b', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{aiError}</span>
          <button onClick={() => setAiError(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#991b1b', fontWeight: 700 }}>&times;</button>
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
        <div style={{ background: '#fafafa', borderRadius: 8, padding: '0.75rem 1rem', border: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: '0.7rem', color: '#9ca3af', textTransform: 'uppercase', marginBottom: 4 }}>Potential Maturity After Remediation</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem' }}>
            <span style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1f2937' }}>{data.current_score}</span>
            <span style={{ fontSize: '1rem', color: '#9ca3af' }}>&rarr;</span>
            <span style={{ fontSize: '1.5rem', fontWeight: 700, color: '#10b981' }}>{data.potential_score}</span>
          </div>
          <ScoreUpliftBar current={data.current_score} potential={data.potential_score} label="All fixes" color="#10b981" />
        </div>
        <div style={{ background: '#fafafa', borderRadius: 8, padding: '0.75rem 1rem', border: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: '0.7rem', color: '#9ca3af', textTransform: 'uppercase', marginBottom: 4 }}>After High Priority Fixes</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem' }}>
            <span style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1f2937' }}>{data.current_score}</span>
            <span style={{ fontSize: '1rem', color: '#9ca3af' }}>&rarr;</span>
            <span style={{ fontSize: '1.5rem', fontWeight: 700, color: '#f59e0b' }}>{data.potential_after_high_priority}</span>
          </div>
          <ScoreUpliftBar current={data.current_score} potential={data.potential_after_high_priority} label="High priority only" color="#f59e0b" />
        </div>
        <div style={{ background: '#fafafa', borderRadius: 8, padding: '0.75rem 1rem', border: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: '0.7rem', color: '#9ca3af', textTransform: 'uppercase', marginBottom: 4 }}>Issues by Priority</div>
          <div style={{ display: 'flex', gap: '1rem', marginTop: 6 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#ef4444' }}>{data.high_priority_count}</div>
              <div style={{ fontSize: '0.65rem', color: '#6b7280' }}>High</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#f59e0b' }}>{data.medium_priority_count}</div>
              <div style={{ fontSize: '0.65rem', color: '#6b7280' }}>Medium</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#3b82f6' }}>{data.low_priority_count}</div>
              <div style={{ fontSize: '0.65rem', color: '#6b7280' }}>Low</div>
            </div>
          </div>
          <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: 8 }}>{data.total_issues} remediation item(s)</div>
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', fontSize: '0.8rem' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: '8px 10px', background: '#f9fafb', borderBottom: '2px solid #e5e7eb', fontSize: '0.7rem', textTransform: 'uppercase', color: '#6b7280' }}>Resource</th>
              <th style={{ textAlign: 'left', padding: '8px 10px', background: '#f9fafb', borderBottom: '2px solid #e5e7eb', fontSize: '0.7rem', textTransform: 'uppercase', color: '#6b7280' }}>Type</th>
              <th style={{ textAlign: 'left', padding: '8px 10px', background: '#f9fafb', borderBottom: '2px solid #e5e7eb', fontSize: '0.7rem', textTransform: 'uppercase', color: '#6b7280' }}>Issue</th>
              <th style={{ textAlign: 'left', padding: '8px 10px', background: '#f9fafb', borderBottom: '2px solid #e5e7eb', fontSize: '0.7rem', textTransform: 'uppercase', color: '#6b7280' }}>Recommendation</th>
              <th style={{ textAlign: 'center', padding: '8px 10px', background: '#f9fafb', borderBottom: '2px solid #e5e7eb', fontSize: '0.7rem', textTransform: 'uppercase', color: '#6b7280' }}>Priority</th>
              <th style={{ textAlign: 'center', padding: '8px 10px', background: '#f9fafb', borderBottom: '2px solid #e5e7eb', fontSize: '0.7rem', textTransform: 'uppercase', color: '#6b7280' }}>Score Impact</th>
              <th style={{ textAlign: 'center', padding: '8px 10px', background: '#f9fafb', borderBottom: '2px solid #e5e7eb', fontSize: '0.7rem', textTransform: 'uppercase', color: '#6b7280' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {resources.map((r, i) => {
              const ps = PRIORITY_STYLE[r.priority] || PRIORITY_STYLE.medium;
              const isConfigGap = r.resource_type === 'Configuration Gap';
              const isAiLoading = aiLoadingIdx === i;
              return (
                <tr key={i} style={{ borderBottom: '1px solid #f3f4f6', background: isConfigGap ? '#fefce8' : 'transparent' }}>
                  <td style={{ padding: '8px 10px', verticalAlign: 'top' }}>
                    <div style={{ fontWeight: 600, fontSize: '0.8rem', color: isConfigGap ? '#92400e' : '#1f2937' }}>{r.resource_name}</div>
                    <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>{isConfigGap ? r.control_id : r.resource_group}</div>
                  </td>
                  <td style={{ padding: '8px 10px', verticalAlign: 'top' }}>
                    <span style={{ fontSize: '0.75rem', color: '#6b7280', ...(isConfigGap ? { fontStyle: 'italic' } : {}) }}>{r.resource_type}</span>
                  </td>
                  <td style={{ padding: '8px 10px', verticalAlign: 'top', maxWidth: 200 }}>
                    <span style={{ fontSize: '0.78rem', color: '#374151' }}>{r.issue}</span>
                  </td>
                  <td style={{ padding: '8px 10px', verticalAlign: 'top', maxWidth: 220 }}>
                    <span style={{ fontSize: '0.78rem', color: '#4b5563' }}>{r.recommendation}</span>
                  </td>
                  <td style={{ padding: '8px 10px', verticalAlign: 'top', textAlign: 'center' }}>
                    <span style={{ padding: '2px 10px', borderRadius: 10, fontSize: '0.7rem', fontWeight: 600, background: ps.bg, color: ps.color }}>
                      {r.priority}
                    </span>
                  </td>
                  <td style={{ padding: '8px 10px', verticalAlign: 'top', textAlign: 'center' }}>
                    <span style={{ fontWeight: 700, color: '#10b981', fontSize: '0.85rem' }}>+{r.score_impact}</span>
                  </td>
                  <td style={{ padding: '8px 10px', verticalAlign: 'top', textAlign: 'center' }}>
                    <button
                      onClick={() => handleAskAI(r, i)}
                      disabled={isAiLoading}
                      title="Ask AI for step-by-step remediation"
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                        padding: '4px 10px', borderRadius: 6, fontSize: '0.7rem', fontWeight: 600,
                        cursor: isAiLoading ? 'wait' : 'pointer',
                        border: '1px solid #c7d2fe', background: isAiLoading ? '#e0e7ff' : '#eef2ff',
                        color: '#4338ca', transition: 'all 0.2s',
                      }}
                      onMouseEnter={e => { if (!isAiLoading) { e.currentTarget.style.background = '#c7d2fe'; } }}
                      onMouseLeave={e => { if (!isAiLoading) { e.currentTarget.style.background = '#eef2ff'; } }}
                    >
                      {isAiLoading ? (
                        <>
                          <span style={{ display: 'inline-block', width: 12, height: 12, border: '2px solid #a5b4fc', borderTopColor: '#4338ca', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                          <span>Analyzing...</span>
                        </>
                      ) : (
                        <>
                          <span style={{ fontSize: '0.85rem' }}>&#10024;</span>
                          <span>Ask AI</span>
                        </>
                      )}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function GovernanceFrameworkContent() {
  const { selectedTenantId } = useTenant();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [expandedPillar, setExpandedPillar] = useState(null);
  const [activeTab, setActiveTab] = useState({});

  const toggleTab = useCallback((key, tab) => {
    setActiveTab(prev => ({ ...prev, [key]: prev[key] === tab ? null : tab }));
  }, []);

  useEffect(() => {
    if (!selectedTenantId) return;
    setLoading(true);
    setError(null);
    getGovernanceFramework(selectedTenantId)
      .then(res => { setData(res.data); setLoading(false); })
      .catch(e => { const d = e.response?.data?.detail; setError(typeof d === 'string' ? d : 'Failed to load governance framework'); setLoading(false); });
  }, [selectedTenantId]);

  if (loading) return <div className="loading-container"><div className="spinner" /><p>Loading governance assessment...</p></div>;
  if (error) return <div className="error-container"><p>{error}</p></div>;
  if (!data?.pillars) return <div className="error-container"><p>No framework data available. Sync tenant data first.</p></div>;

  const pillars = data.pillars;
  const pillarEntries = Object.entries(pillars);
  const radarData = pillarEntries.map(([, p]) => ({
    pillar: p.name.split(' ')[0],
    score: p.maturity_score || (p.score / 20),
    fullName: p.name,
  }));

  const overallPct = data.overall_percentage || data.overall_score || 0;
  const overallMaturity = data.overall_maturity || 0;
  const overallRisk = data.overall_risk || 'high';
  const totalItems = data.total_items || data.total_controls || 0;
  const totalPassed = data.passed || 0;
  const totalPillars = data.total_pillars || pillarEntries.length;
  const cloudProvider = (data.cloud_provider || 'azure').toLowerCase();
  const providerLabel = cloudProvider === 'aws' ? 'AWS' : cloudProvider === 'gcp' ? 'GCP' : 'Azure';
  const providerModelName = cloudProvider === 'aws' ? 'AWS Well-Architected Landing Zone' : cloudProvider === 'gcp' ? 'Google Cloud Landing Zone' : 'Azure Landing Zone';

  return (
    <div>
      <div className="page-header">
        <h1>Landing Zone Governance Assessment</h1>
        <p style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          8-Pillar {providerModelName} Governance Maturity Model
          <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: '0.7rem', fontWeight: 700, background: cloudProvider === 'aws' ? '#ff9900' : cloudProvider === 'gcp' ? '#4285f4' : '#0078d4', color: '#fff' }}>
            {providerLabel}
          </span>
        </p>
      </div>

      <AIGovernanceDetailedReport assessmentData={data} />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
        <div className="card" style={{ display: 'flex', alignItems: 'center', gap: '2rem' }}>
          <OverallGauge percentage={overallPct} maturity={overallMaturity} risk={overallRisk} />
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '1rem' }}>Overall Governance Maturity</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <div>
                <div style={{ fontSize: '0.75rem', color: '#9ca3af', textTransform: 'uppercase' }}>Pillars</div>
                <div style={{ fontSize: '1.3rem', fontWeight: 700 }}>{totalPillars}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.75rem', color: '#9ca3af', textTransform: 'uppercase' }}>Assessment Items</div>
                <div style={{ fontSize: '1.3rem', fontWeight: 700 }}>{totalItems}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.75rem', color: '#9ca3af', textTransform: 'uppercase' }}>Mature (4+)</div>
                <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#10b981' }}>{totalPassed}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.75rem', color: '#9ca3af', textTransform: 'uppercase' }}>Needs Attention</div>
                <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#ef4444' }}>{totalItems - totalPassed}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <h3 style={{ marginBottom: '0.75rem', fontSize: '1rem' }}>Pillar Maturity Radar</h3>
          <ResponsiveContainer width="100%" height={250}>
            <RadarChart data={radarData}>
              <PolarGrid stroke="#e5e7eb" />
              <PolarAngleAxis dataKey="pillar" tick={{ fontSize: 10, fill: '#6b7280' }} />
              <PolarRadiusAxis domain={[0, 5]} tickCount={6} tick={{ fontSize: 9, fill: '#9ca3af' }} />
              <Radar name="Maturity" dataKey="score" stroke="#4f46e5" fill="#4f46e5" fillOpacity={0.25} strokeWidth={2} />
              <Tooltip formatter={(v) => `${v}/5`} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ marginBottom: '1rem', fontSize: '1rem' }}>Pillar Maturity Overview</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' }}>
          {pillarEntries.map(([key, p]) => {
            const rs = RISK_STYLE[p.risk_level] || RISK_STYLE.high;
            const pct = p.score || 0;
            return (
              <div key={key} style={{
                padding: '1rem', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fafafa',
                cursor: 'pointer', transition: 'box-shadow 0.2s',
              }}
                onClick={() => setExpandedPillar(expandedPillar === key ? null : key)}
                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '1.2rem' }}>{PILLAR_ICONS[key]}</span>
                  <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', flex: 1, lineHeight: 1.2 }}>{p.name}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <MaturityBadge score={p.maturity_score || 0} />
                  <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: '0.65rem', fontWeight: 600, background: rs.bg, color: rs.color }}>
                    {rs.label}
                  </span>
                </div>
                <div style={{ width: '100%', marginTop: '0.25rem' }}>
                  <div className="progress-bar" style={{ height: 6 }}>
                    <div className="progress-bar-fill" style={{ width: `${pct}%`, background: PILLAR_COLORS[key] || '#6b7280', transition: 'width 0.6s ease' }} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.25rem' }}>
                    <span style={{ fontSize: '0.65rem', color: '#9ca3af' }}>{p.passed}/{p.total_items} mature</span>
                    <span style={{ fontSize: '0.7rem', fontWeight: 600, color: PILLAR_COLORS[key] }}>{pct}%</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {pillarEntries.map(([key, pillar]) => {
        const currentTab = activeTab[key] || 'checklist';
        return (
          <div key={key} className="card" style={{ marginBottom: '1rem' }}>
            <div
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }}
              onClick={() => setExpandedPillar(expandedPillar === key ? null : key)}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{ width: 6, height: 40, borderRadius: 3, background: PILLAR_COLORS[key] || '#6b7280' }} />
                <span style={{ fontSize: '1.4rem' }}>{PILLAR_ICONS[key]}</span>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1rem' }}>{pillar.name}</h3>
                  <p style={{ margin: '2px 0 0', fontSize: '0.78rem', color: '#6b7280' }}>{pillar.description}</p>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <MaturityBadge score={pillar.maturity_score || 0} />
                <span style={{
                  padding: '2px 10px', borderRadius: 10, fontSize: '0.7rem', fontWeight: 600,
                  background: (RISK_STYLE[pillar.risk_level] || RISK_STYLE.high).bg,
                  color: (RISK_STYLE[pillar.risk_level] || RISK_STYLE.high).color,
                }}>
                  {(RISK_STYLE[pillar.risk_level] || RISK_STYLE.high).label}
                </span>
                <span style={{ fontWeight: 700, fontSize: '1rem', color: PILLAR_COLORS[key] }}>{pillar.score || 0}%</span>
                <span style={{ fontSize: '1rem', color: '#9ca3af', transform: expandedPillar === key ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                  &#9660;
                </span>
              </div>
            </div>

            {expandedPillar === key && (
              <div style={{ marginTop: '1rem', borderTop: '1px solid #e5e7eb', paddingTop: '1rem' }}>
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleTab(key, 'checklist'); }}
                    style={{
                      padding: '6px 16px', borderRadius: 6, fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
                      border: currentTab === 'checklist' ? `2px solid ${PILLAR_COLORS[key]}` : '1px solid #d1d5db',
                      background: currentTab === 'checklist' ? `${PILLAR_COLORS[key]}15` : '#fff',
                      color: currentTab === 'checklist' ? PILLAR_COLORS[key] : '#6b7280',
                    }}
                  >
                    Assessment Checklist
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleTab(key, 'remediation'); }}
                    style={{
                      padding: '6px 16px', borderRadius: 6, fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
                      border: currentTab === 'remediation' ? `2px solid ${PILLAR_COLORS[key]}` : '1px solid #d1d5db',
                      background: currentTab === 'remediation' ? `${PILLAR_COLORS[key]}15` : '#fff',
                      color: currentTab === 'remediation' ? PILLAR_COLORS[key] : '#6b7280',
                    }}
                  >
                    Resource Impact & Remediation
                  </button>
                </div>

                {currentTab === 'checklist' && (
                  <>
                    <div style={{ background: '#f0f4ff', borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1rem', border: '1px solid #dbeafe' }}>
                      <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#3b82f6', textTransform: 'uppercase', marginBottom: '0.25rem' }}>What to Assess</div>
                      <div style={{ fontSize: '0.82rem', color: '#374151' }}>{pillar.what_to_assess}</div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {(pillar.items || pillar.controls || []).map((item, i) => {
                        const score = item.maturity_score || 0;
                        let scoreBg, scoreColor;
                        if (score >= 4) { scoreBg = '#dcfce7'; scoreColor = '#166534'; }
                        else if (score >= 3) { scoreBg = '#fef3c7'; scoreColor = '#92400e'; }
                        else { scoreBg = '#fee2e2'; scoreColor = '#991b1b'; }

                        return (
                          <div key={i} style={{
                            borderRadius: 8, border: '1px solid #e5e7eb',
                            background: item.checked ? '#f0fdf4' : '#fff',
                            overflow: 'hidden',
                          }}>
                            <div style={{
                              display: 'flex', alignItems: 'center', gap: '0.75rem',
                              padding: '0.7rem 1rem',
                            }}>
                              <div style={{
                                width: 22, height: 22, borderRadius: 4, border: `2px solid ${item.checked ? '#10b981' : '#d1d5db'}`,
                                background: item.checked ? '#10b981' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                flexShrink: 0,
                              }}>
                                {item.checked && <span style={{ color: '#fff', fontSize: '0.7rem', fontWeight: 700 }}>&#10003;</span>}
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                                  <span style={{ fontFamily: 'monospace', fontSize: '0.7rem', color: '#9ca3af' }}>{item.id}</span>
                                  <span style={{ fontWeight: 600, fontSize: '0.85rem', color: '#1f2937' }}>{item.name}</span>
                                  <span className={`badge badge-${item.severity}`} style={{ fontSize: '0.6rem' }}>{item.severity}</span>
                                </div>
                                <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: '2px' }}>{item.description}</div>
                                {item.detail && (
                                  <div style={{ fontSize: '0.75rem', color: '#4b5563', marginTop: '4px', fontStyle: 'italic' }}>{item.detail}</div>
                                )}
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', flexShrink: 0 }}>
                                <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 700, background: scoreBg, color: scoreColor }}>
                                  {score}/5
                                </span>
                                <div style={{ width: 50, height: 4, borderRadius: 2, background: '#e5e7eb', marginTop: 2 }}>
                                  <div style={{ width: `${(score / 5) * 100}%`, height: '100%', borderRadius: 2, background: scoreColor, transition: 'width 0.4s' }} />
                                </div>
                              </div>
                            </div>
                            {item.scoring_explanation && (() => {
                              const parts = item.scoring_explanation.split('|MISSING|');
                              const mainExplanation = parts[0]?.trim();
                              const missingReason = parts[1]?.trim();
                              const score = item.maturity_score || 0;
                              const isFull = score === 5;
                              return (
                                <div style={{
                                  padding: '0.5rem 1rem 0.6rem 3.25rem',
                                  background: '#f8fafc', borderTop: '1px solid #f1f5f9',
                                  fontSize: '0.73rem', color: '#64748b', lineHeight: 1.6,
                                }}>
                                  <span style={{ fontWeight: 600, color: '#475569', marginRight: '0.35rem' }}>How this is scored:</span>
                                  {mainExplanation}
                                  {missingReason && !isFull && (
                                    <div style={{
                                      marginTop: '0.4rem',
                                      padding: '0.35rem 0.6rem',
                                      background: score >= 3 ? '#fffbeb' : score >= 1 ? '#fef2f2' : '#f1f5f9',
                                      border: `1px solid ${score >= 3 ? '#fde68a' : score >= 1 ? '#fecaca' : '#e2e8f0'}`,
                                      borderRadius: '4px',
                                      color: score >= 3 ? '#92400e' : score >= 1 ? '#991b1b' : '#475569',
                                      fontWeight: 500,
                                    }}>
                                      <span style={{ fontWeight: 700, marginRight: '0.3rem' }}>
                                        {score >= 3 ? `Why not 5/5:` : `Why ${score}/5:`}
                                      </span>
                                      {missingReason}
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        );
                      })}
                    </div>

                    <div style={{ display: 'flex', gap: '1.5rem', marginTop: '1rem', paddingTop: '0.75rem', borderTop: '1px solid #f3f4f6' }}>
                      <span style={{ fontSize: '0.8rem' }}><span style={{ color: '#10b981', fontWeight: 600 }}>{pillar.passed}</span> <span style={{ color: '#6b7280' }}>mature</span></span>
                      <span style={{ fontSize: '0.8rem' }}><span style={{ color: '#f59e0b', fontWeight: 600 }}>{pillar.warnings}</span> <span style={{ color: '#6b7280' }}>needs work</span></span>
                      <span style={{ fontSize: '0.8rem' }}><span style={{ color: '#ef4444', fontWeight: 600 }}>{pillar.failed}</span> <span style={{ color: '#6b7280' }}>at risk</span></span>
                    </div>
                  </>
                )}

                {currentTab === 'remediation' && (
                  <RemediationPanel tenantId={selectedTenantId} pillarKey={key} pillarColor={PILLAR_COLORS[key]} pillarName={pillar.name} cloudProvider={cloudProvider} />
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function GovernanceFramework() {
  return <NoTenantGuard><GovernanceFrameworkContent /></NoTenantGuard>;
}
